import { useNavigate } from "react-router-dom";
import {
  ArrowRight, HardHat, ClipboardCheck, Mail, ShieldCheck, AlertTriangle,
  Clock, CheckCircle2, Lock, Truck, FileText, Search, Sparkles,
  Eye, Calendar, Zap, Target, BarChart3, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { StartDemoButton } from "@/components/shared/LiveDemoTour";

function MiniDashboard() {
  return (
    <div className="relative w-full" data-testid="hero-mini-dashboard">
      <div className="bg-white rounded-2xl shadow-2xl shadow-slate-900/12 border border-slate-200/80 overflow-hidden">
        <div className="bg-slate-900 px-4 py-2.5 flex items-center gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="text-[11px] text-slate-400 ml-2 font-mono">easypermit / builder / stage-readiness</span>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-red-500" />
              <div>
                <p className="text-[11px] font-semibold text-red-800">Pre-Slab: Not Ready</p>
                <p className="text-[9px] text-red-600">3 blockers preventing stage clearance</p>
              </div>
            </div>
            <div className="flex gap-1.5">
              <span className="inline-flex items-center gap-1 bg-red-100 rounded-full px-2 py-0.5 text-[9px] font-medium text-red-700">
                <Truck className="w-2.5 h-2.5" /> Trade locked
              </span>
              <span className="inline-flex items-center gap-1 bg-amber-100 rounded-full px-2 py-0.5 text-[9px] font-medium text-amber-700">
                <ClipboardCheck className="w-2.5 h-2.5" /> Surveyor: wait
              </span>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: "Blocked", value: "3", color: "text-red-700", bg: "bg-red-50", border: "border-red-100" },
              { label: "Proof waiting", value: "4", color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-100" },
              { label: "In progress", value: "6", color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-100" },
              { label: "Cleared", value: "42", color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-100" },
            ].map((s, i) => (
              <div key={i} className={`${s.bg} border ${s.border} rounded-lg p-2`}>
                <p className={`text-[10px] ${s.color} font-medium`}>{s.label}</p>
                <p className={`text-lg font-heading font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-3 py-1.5 grid grid-cols-12 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              <span className="col-span-1">#</span>
              <span className="col-span-4">Condition</span>
              <span className="col-span-2">Owner</span>
              <span className="col-span-2">Due</span>
              <span className="col-span-3">Status</span>
            </div>
            {[
              { num: "2.1", title: "Drainage Certificate", owner: "D. Patel", due: "20/01", status: "needs review", cls: "bg-amber-50 text-amber-700" },
              { num: "2.2", title: "Soil Report Verification", owner: "D. Patel", due: "18/01", status: "blocker", cls: "bg-red-50 text-red-700" },
              { num: "2.3", title: "Stormwater Connection", owner: "S. Chen", due: "22/01", status: "blocker", cls: "bg-red-50 text-red-700" },
              { num: "2.4", title: "Setback Confirmation", owner: "M. Stevens", due: "25/01", status: "in progress", cls: "bg-blue-50 text-blue-700" },
            ].map((row, i) => (
              <div key={i} className={`px-3 py-2 grid grid-cols-12 items-center text-[11px] border-t border-slate-100`}>
                <span className="col-span-1 font-mono text-slate-400">{row.num}</span>
                <span className="col-span-4 font-medium text-slate-800 truncate">{row.title}</span>
                <span className="col-span-2 text-slate-500">{row.owner}</span>
                <span className="col-span-2 text-slate-500">{row.due}</span>
                <span className="col-span-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${row.cls}`}>
                    {row.status === "blocker" && <span className="w-1 h-1 rounded-full bg-red-500 mr-1 animate-pulse" />}
                    {row.status}
                  </span>
                </span>
              </div>
            ))}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-lg border border-blue-200 flex items-center justify-center shrink-0">
              <Eye className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-semibold text-blue-800 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Visual Proof Assist</p>
              <p className="text-[9px] text-blue-600">Footing_Photo_01.jpg: likely visible, review recommended</p>
            </div>
            <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">87%</span>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 space-y-1">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Next best actions</p>
            <p className="text-[10px] text-slate-600">1. Chase David Patel for soil report</p>
            <p className="text-[10px] text-slate-600">2. Follow up stormwater connection</p>
            <p className="text-[10px] text-slate-600">3. Review uploaded drainage certificate</p>
          </div>
        </div>
      </div>

      {/* Floating notification cards */}
      <div className="absolute -right-6 top-4 dashboard-card-float" style={{ animationDelay: "0s" }}>
        <div className="glass-card rounded-xl px-3 py-2.5 shadow-lg flex items-center gap-2.5 max-w-[210px]">
          <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
            <Mail className="w-3.5 h-3.5 text-blue-600" />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-800">Council email captured</p>
            <p className="text-[9px] text-slate-500">Wyndham Council - Request detected</p>
          </div>
        </div>
      </div>
      <div className="absolute -left-6 top-20 dashboard-card-float" style={{ animationDelay: "1s" }}>
        <div className="glass-card rounded-xl px-3 py-2.5 shadow-lg flex items-center gap-2.5 max-w-[200px]">
          <div className="w-7 h-7 bg-amber-100 rounded-lg flex items-center justify-center shrink-0">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-800">Waiting on engineer</p>
            <p className="text-[9px] text-slate-500">Soil report 2 days overdue</p>
          </div>
        </div>
      </div>
      <div className="absolute -right-4 top-[220px] dashboard-card-float" style={{ animationDelay: "1.8s" }}>
        <div className="glass-card rounded-xl px-3 py-2.5 shadow-lg flex items-center gap-2.5 max-w-[210px]">
          <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
            <Lock className="w-3.5 h-3.5 text-red-600" />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-800">Trade release locked</p>
            <p className="text-[9px] text-slate-500">Concreter on hold until blockers clear</p>
          </div>
        </div>
      </div>
      <div className="absolute -left-4 bottom-24 dashboard-card-float" style={{ animationDelay: "2.5s" }}>
        <div className="glass-card rounded-xl px-3 py-2.5 shadow-lg flex items-center gap-2.5 max-w-[210px]">
          <div className="w-7 h-7 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-800">Certificate missing</p>
            <p className="text-[9px] text-slate-500">Drainage cert still needed for Pre-Slab</p>
          </div>
        </div>
      </div>
      <div className="absolute -right-2 bottom-8 dashboard-card-float" style={{ animationDelay: "3.2s" }}>
        <div className="glass-card rounded-xl px-3 py-2.5 shadow-lg flex items-center gap-2.5 max-w-[210px]">
          <div className="w-7 h-7 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-800">Proof waiting review</p>
            <p className="text-[9px] text-slate-500">Site photo uploaded via QR</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function TrustBar() {
  const metrics = [
    { value: '11', label: 'conditions extracted per permit', icon: FileText },
    { value: '4', label: 'build stages tracked', icon: Target },
    { value: '87%', label: 'AI proof match accuracy', icon: Sparkles },
    { value: '< 2min', label: 'from email to tracked item', icon: Zap },
  ];

  return (
    <div className="bg-slate-900 border-y border-slate-800" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((m, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-800 rounded-xl flex items-center justify-center shrink-0">
                <m.icon className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-lg font-heading font-bold text-white">{m.value}</p>
                <p className="text-[11px] text-slate-400">{m.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <>
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 hero-grid-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-transparent to-white/80" />
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-blue-50 rounded-full blur-[100px] opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-slate-100 rounded-full blur-[80px] opacity-40 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-32 lg:py-36 w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-4 py-1.5 mb-8 shadow-lg shadow-slate-900/20">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Approvals and Readiness Operating System</span>
              </div>

              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-[3.6rem] leading-[1.08] tracking-tight text-slate-900 mb-6"
                data-testid="hero-headline">
                Manage what quietly stops the build.
              </h1>

              <p className="text-base lg:text-lg text-slate-600 leading-relaxed mb-8" data-testid="hero-subheadline">
                Council requests. Approval conditions. Missing proof and certificates. easypermit shows whether the next stage is actually ready before trades are released or the surveyor is notified.
              </p>

              <ul className="space-y-3 mb-6" data-testid="hero-bullets">
                {[
                  "Know what is blocking slab, frame, or final",
                  "Know who is holding the job up",
                  "Know when the stage is actually ready",
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
                    <span className="text-sm text-slate-700 font-medium">{bullet}</span>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-slate-400 mb-10 italic">
                Built to sit on top of Outlook, Gmail, PDFs, and the way your team already works.
              </p>

              <div className="flex flex-col sm:flex-row gap-3" data-testid="hero-cta-area">
                <Button
                  size="lg"
                  className="bg-slate-900 text-white hover:bg-slate-800 shadow-xl shadow-slate-900/15 h-14 px-7 text-sm font-semibold gap-3 group"
                  onClick={() => navigate("/builder")}
                  data-testid="explore-builder-btn"
                >
                  <HardHat className="w-5 h-5" />
                  Explore Builder View
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 h-14 px-7 text-sm font-semibold gap-3 group"
                  onClick={() => navigate("/surveyor")}
                  data-testid="explore-surveyor-btn"
                >
                  <ClipboardCheck className="w-5 h-5" />
                  Explore Surveyor View
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </div>
              <div className="mt-4">
                <StartDemoButton variant="landing" />
              </div>
            </div>

            <div className="hidden lg:block">
              <MiniDashboard />
            </div>
          </div>
        </div>
      </section>
      <TrustBar />
    </>
  );
}
